import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Customer } from "@/types/general";
import { cn } from "@helpers/utils";
import BaseTable from "@reusables/BaseTable";
import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import BaseMaterialButton from "@reusables/BaseMaterialButton";

const CUSTOMERS_PER_SUB_TABLE = 10;
const SUB_TABLES_PER_PAGE = 2;
const CUSTOMERS_PER_PAGE = CUSTOMERS_PER_SUB_TABLE * SUB_TABLES_PER_PAGE;

interface CustomerMultiTableProps {
    customers: Customer.Slim[];
    onAddClick?: () => void;
    onRemoveCustomer?: (customer: Customer.Slim) => void;
}

function CustomerMultiTable({ customers, onAddClick, onRemoveCustomer }: CustomerMultiTableProps) {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation.customerTable" });

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const previousCustomersLengthRef = useRef(customers.length);
    const previousLastCustomerIdRef = useRef(customers[customers.length - 1]?.id);

    const filteredCustomers = useMemo(
        () => customers.filter((customer) => isCustomerMatchingSearch(customer, search)),
        [customers, search]
    );

    const pageCount = Math.max(1, Math.ceil(filteredCustomers.length / CUSTOMERS_PER_PAGE));
    const hasCustomers = customers.length > 0;
    const hasFilteredCustomers = filteredCustomers.length > 0;
    const shouldShowPagination = filteredCustomers.length > CUSTOMERS_PER_PAGE;

    useEffect(() => {
        setPage(1);
    }, [search]);

    useEffect(() => {
        const lastCustomer = customers[customers.length - 1];
        const previousCustomersLength = previousCustomersLengthRef.current;
        const previousLastCustomerId = previousLastCustomerIdRef.current;

        if (
            customers.length > previousCustomersLength &&
            lastCustomer?.id !== previousLastCustomerId &&
            isCustomerMatchingSearch(lastCustomer, search)
        ) {
            setPage(pageCount);
        }

        previousCustomersLengthRef.current = customers.length;
        previousLastCustomerIdRef.current = lastCustomer?.id;
    }, [customers, pageCount, search]);

    useEffect(() => {
        setPage((currentPage) => Math.min(currentPage, pageCount));
    }, [pageCount]);

    const pageCustomers = useMemo(() => {
        const start = (page - 1) * CUSTOMERS_PER_PAGE;
        return filteredCustomers.slice(start, start + CUSTOMERS_PER_PAGE);
    }, [filteredCustomers, page]);

    const subTables = useMemo(() => chunkCustomers(pageCustomers), [pageCustomers]);

    if (!hasCustomers) {
        return (
            <section className="t-resize border border-solid border-gray-300 rounded-[8px] p-[16px] overflow-hidden">
                <div className="flex justify-center">
                    <div className="flex h-[128px] flex-col items-center justify-center space-y-[24px]">
                        <div className="text-2xl font-thin text-center text-gray-300">
                            {t("noCustomers")}
                        </div>

                        {onAddClick ? (
                            <div className="mt-[16px]">
                                <BaseMaterialButton
                                    type="button"
                                    onClick={onAddClick}
                                    fontSize={14}
                                    className="w-[200px] h-[48px] space-x-2"
                                >
                                    <Plus size={16} aria-hidden="true" />
                                    <span>{t("buttons.addCustomer")}</span>
                                </BaseMaterialButton>
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="t-resize border border-solid border-gray-300 rounded-[8px] p-[16px] overflow-hidden">
            <div className="flex flex-col gap-[16px]">
                <div className="flex flex-col gap-[12px] desktop:flex-row desktop:items-center desktop:justify-between">
                    <div className="flex items-center gap-[8px]">
                        <div className="text-lg font-semibold text-accent">{t("heading")}</div>
                        <div className="rounded-[16px] bg-[#E5E5E5] px-[8px] py-[2px] text-sm font-thin text-accent">
                            {customers.length}
                        </div>
                    </div>

                    <div className="flex flex-col gap-[12px] desktop:flex-row desktop:items-center">
                        <label className="relative block w-full desktop:w-[300px]">
                            <Search
                                size={16}
                                className="absolute left-[12px] top-[12px] text-gray-600"
                                aria-hidden="true"
                            />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={t("searchPlaceholder")}
                                className="h-[40px] w-full rounded-[8px] border border-solid border-gray-300 bg-white pl-[38px] pr-[12px] text-sm text-accent outline-none transition-colors duration-150 placeholder:text-inputs-placeholder focus:border-primary-purple"
                            />
                        </label>

                        {onAddClick ? (
                            <BaseMaterialButton
                                type="button"
                                onClick={onAddClick}
                                fontSize={14}
                                fontWeight={400}
                                className={"space-x-2 h-[40px]"}
                            >
                                <Plus size={16} aria-hidden="true" />
                                <span>{t("buttons.addCustomer")}</span>
                            </BaseMaterialButton>
                        ) : null}
                    </div>
                </div>

                {hasFilteredCustomers ? (
                    <>
                        <div className="flex flex-col gap-[12px] desktop:flex-row">
                            {subTables.map((subTableCustomers, index) => (
                                <CustomerSubTable
                                    key={`${page}-${index}-${subTableCustomers[0]?.id ?? index}`}
                                    customers={subTableCustomers}
                                    startIndex={
                                        (page - 1) * CUSTOMERS_PER_PAGE +
                                        index * CUSTOMERS_PER_SUB_TABLE
                                    }
                                    tableCount={subTables.length}
                                    onRemoveCustomer={onRemoveCustomer}
                                />
                            ))}
                        </div>

                        {shouldShowPagination ? (
                            <div className="flex items-center justify-end gap-[12px]">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setPage((currentPage) => Math.max(1, currentPage - 1))
                                    }
                                    disabled={page === 1}
                                    aria-label={t("pagination.previousPage")}
                                    className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[8px] border border-solid border-gray-300 text-accent transition-colors duration-150 hover:border-primary-purple disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300"
                                >
                                    <ChevronLeft size={16} aria-hidden="true" />
                                </button>

                                <div className="text-sm font-light text-gray-600">
                                    {t("pagination.page")} {page} {t("pagination.of")} {pageCount}
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setPage((currentPage) =>
                                            Math.min(pageCount, currentPage + 1)
                                        )
                                    }
                                    disabled={page === pageCount}
                                    aria-label={t("pagination.nextPage")}
                                    className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[8px] border border-solid border-gray-300 text-accent transition-colors duration-150 hover:border-primary-purple disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300"
                                >
                                    <ChevronRight size={16} aria-hidden="true" />
                                </button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="flex min-h-[96px] items-center justify-center text-xl font-thin text-gray-300">
                        {t("noResults")}
                    </div>
                )}
            </div>
        </section>
    );
}

interface CustomerSubTableProps {
    customers: Customer.Slim[];
    startIndex: number;
    tableCount: number;
    onRemoveCustomer?: (customer: Customer.Slim) => void;
}

function CustomerSubTable({
    customers,
    startIndex,
    tableCount,
    onRemoveCustomer,
}: CustomerSubTableProps) {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation.customerTable" });

    return (
        <div
            className={cn(
                "t-resize w-full overflow-hidden",
                getSubTableWidthClass(tableCount),
                tableCount > 1 && "rounded-[8px] border border-solid border-gray-300"
            )}
        >
            <BaseTable
                data={customers}
                immutable
                rowKeyGetter={(customer) => customer.id}
                columns={[
                    {
                        header: t("columns.0"),
                        getter: (_customer, index) => _customer.code,
                        sx: {
                            width: 44,
                            paddingLeft: "10px",
                            paddingRight: "10px",
                        },
                    },
                    {
                        header: t("columns.1"),
                        getter: (customer) => customer.name,
                    },
                    {
                        visible: !!onRemoveCustomer,
                        header: t("columns.2"),
                        getter: (customer) => (
                            <TrashSVG
                                className="mx-auto block cursor-pointer text-tables-secondaryIcon hover:text-tables-highlightedIcon"
                                onClick={() => onRemoveCustomer?.(customer)}
                            />
                        ),
                        sx: {
                            width: 48,
                            textAlign: "center",
                            paddingLeft: "10px",
                            paddingRight: "10px",
                        },
                    },
                ]}
                size="small"
                headerSx={{
                    background: "#F9F9F9",
                }}
                sx={{
                    "& .MuiTableCell-root": {
                        paddingTop: "9px",
                        paddingBottom: "9px",
                        fontSize: "14px",
                    },
                    "& .MuiTableCell-head": {
                        color: "#686868",
                        fontWeight: 600,
                    },
                    "& .MuiTableCell-head:nth-of-type(1)": {
                        textAlign: "center",
                        width: 44,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                    },
                    "& .MuiTableCell-head:nth-of-type(3)": {
                        textAlign: "center",
                        width: 48,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                    },
                }}
            />
        </div>
    );
}

function chunkCustomers(customers: Customer.Slim[]): Customer.Slim[][] {
    const chunks: Customer.Slim[][] = [];

    for (let index = 0; index < customers.length; index += CUSTOMERS_PER_SUB_TABLE) {
        chunks.push(customers.slice(index, index + CUSTOMERS_PER_SUB_TABLE));
    }

    return chunks;
}

function getSubTableWidthClass(tableCount: number): string {
    return tableCount === 1 ? "desktop:w-full" : "desktop:w-1/2";
}

function isCustomerMatchingSearch(customer: Customer.Slim, search: string): boolean {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
        return true;
    }

    const normalizedName = customer.name.toLowerCase();

    if (normalizedName.includes(normalizedSearch)) {
        return true;
    }

    let searchIndex = 0;

    for (const character of normalizedName) {
        if (character === normalizedSearch[searchIndex]) {
            searchIndex += 1;
        }

        if (searchIndex === normalizedSearch.length) {
            return true;
        }
    }

    return false;
}

export default React.memo(CustomerMultiTable);
